import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Users, 
  FileCheck, 
  AlertTriangle, 
  Clock, 
  Plus,
  ChevronRight,
  GraduationCap,
  Stethoscope,
  Shield,
  Truck,
  Package,
  Target,
  Wrench
} from 'lucide-react';
import api from '@/services/api';
import { toast } from 'sonner';
import { useApp } from '@/context/AppContext';

const KPICard = ({ title, value, icon: Icon, color, subtitle, trend }) => (
  <Card className="relative overflow-hidden">
    <CardContent className="p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-500">{title}</p>
          <p className="text-3xl font-bold mt-1">{value}</p>
          {subtitle && <p className="text-sm text-gray-400 mt-1">{subtitle}</p>}
        </div>
        <div className={`p-3 rounded-full ${color}`}>
          <Icon className="h-6 w-6 text-white" />
        </div>
      </div>
      {trend && (
        <div className="mt-4 pt-4 border-t">
          <span className={`text-sm ${trend > 0 ? 'text-green-600' : 'text-red-600'}`}>
            {trend > 0 ? '+' : ''}{trend}% vs mois dernier
          </span>
        </div>
      )}
    </CardContent>
  </Card>
);

const ExpirationItem = ({ item }) => {
  const daysUntil = Math.ceil((new Date(item.date_fin) - new Date()) / (1000 * 60 * 60 * 24));
  const isUrgent = daysUntil <= 14;
  
  return (
    <div className={`p-4 rounded-lg border ${isUrgent ? 'border-red-200 bg-red-50' : 'border-orange-200 bg-orange-50'}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-full ${isUrgent ? 'bg-red-100' : 'bg-orange-100'}`}>
            {item.type === 'CACES' && <Shield className={`h-4 w-4 ${isUrgent ? 'text-red-600' : 'text-orange-600'}`} />}
            {item.type === 'Formation' && <GraduationCap className={`h-4 w-4 ${isUrgent ? 'text-red-600' : 'text-orange-600'}`} />}
            {item.type === 'Visite médicale' && <Stethoscope className={`h-4 w-4 ${isUrgent ? 'text-red-600' : 'text-orange-600'}`} />}
            {item.type === 'Autorisation' && <FileCheck className={`h-4 w-4 ${isUrgent ? 'text-red-600' : 'text-orange-600'}`} />}
          </div>
          <div>
            <p className="font-medium text-gray-900">{item.collaborateur}</p>
            <p className="text-sm text-gray-500">{item.type}: {item.details}</p>
          </div>
        </div>
        <Badge variant={isUrgent ? 'destructive' : 'secondary'}>
          {daysUntil <= 0 ? 'Échu' : `${daysUntil}j`}
        </Badge>
      </div>
    </div>
  );
};

export default function Dashboard() {
  const { hasRole } = useApp();
  const [stats, setStats] = useState({
    total_collaborateurs: 0,
    autorisations_actives: 0,
    demandes_en_cours: 0,
    expirations_30j: 0,
    formations_expirant: 0,
    caces_expirant: 0,
    visites_expirant: 0
  });
  const [expirations, setExpirations] = useState([]);
  const [recentDemandes, setRecentDemandes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const [statsData, expirationsData, demandesData] = await Promise.all([
        api.getDashboardStats(),
        api.getUpcomingExpirations(),
        api.getDemandes()
      ]);
      
      setStats(statsData);
      setExpirations(expirationsData.slice(0, 5));
      // Filtrer pour ne garder que les vraies demandes en cours (pas les délivrées, archivées, expirées ou rejetées)
      const validStatuts = ['Brouillon', 'À valider HSE', 'En signature', 'À signer'];
      setRecentDemandes(demandesData.filter(d => validStatuts.includes(d.statut)).slice(0, 5));
    } catch (error) {
      console.error('Error loading dashboard:', error);
      toast.error('Erreur lors du chargement du tableau de bord');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Tableau de bord</h1>
          <p className="text-gray-500">Gestion des collaborateurs et des compétences</p>
        </div>
        <Link to="/nouvelle-autorisation">
          <Button className="gap-2">
            <Plus className="h-4 w-4" />
            Nouvelle autorisation
          </Button>
        </Link>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <KPICard
          title="Collaborateurs"
          value={stats.total_collaborateurs}
          icon={Users}
          color="bg-blue-500"
          subtitle="Actifs"
        />
        <KPICard
          title="Autorisations actives"
          value={stats.autorisations_actives}
          icon={FileCheck}
          color="bg-green-500"
          subtitle="Délivrées"
        />
        <KPICard
          title="Demandes en cours"
          value={stats.demandes_en_cours}
          icon={Clock}
          color="bg-orange-500"
          subtitle="À traiter"
        />
        <KPICard
          title="Expirations < 30j"
          value={stats.expirations_30j}
          icon={AlertTriangle}
          color="bg-red-500"
          subtitle="Attention requise"
        />
      </div>

      {/* Secondary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-full bg-purple-100">
                <GraduationCap className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Formations expirant</p>
                <p className="text-2xl font-bold">{stats.formations_expirant}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-full bg-teal-100">
                <Shield className="h-6 w-6 text-teal-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">CACES expirant</p>
                <p className="text-2xl font-bold">{stats.caces_expirant}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-full bg-pink-100">
                <Stethoscope className="h-6 w-6 text-pink-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Visites expirant</p>
                <p className="text-2xl font-bold">{stats.visites_expirant}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Upcoming Expirations */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg">Échéances proches</CardTitle>
            <Link to="/autorisations" className="text-sm text-blue-600 hover:underline flex items-center gap-1">
              Voir tout <ChevronRight className="h-4 w-4" />
            </Link>
          </CardHeader>
          <CardContent>
            {expirations.length > 0 ? (
              <div className="space-y-3">
                {expirations.map((item, index) => (
                  <ExpirationItem key={index} item={item} />
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <AlertTriangle className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>Aucune échéance dans les 60 prochains jours</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Demands */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg">Demandes en cours</CardTitle>
            <Link to="/demandes" className="text-sm text-blue-600 hover:underline flex items-center gap-1">
              Voir tout <ChevronRight className="h-4 w-4" />
            </Link>
          </CardHeader>
          <CardContent>
            {recentDemandes.length > 0 ? (
              <div className="space-y-3">
                {recentDemandes.map((demande) => (
                  <Link 
                    key={demande.id} 
                    to={`/demandes/${demande.id}`}
                    className="block p-4 rounded-lg border hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-gray-900">
                          {demande.collaborateur?.prenom} {demande.collaborateur?.nom}
                        </p>
                        <p className="text-sm text-gray-500">
                          {demande.numero} • {new Date(demande.date_creation).toLocaleDateString('fr-FR')}
                        </p>
                      </div>
                      <Badge variant={
                        demande.statut === 'En signature' ? 'default' :
                        demande.statut === 'À valider HSE' ? 'secondary' :
                        'outline'
                      }>
                        {demande.statut}
                      </Badge>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <FileCheck className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>Aucune demande en cours</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Actions rapides</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {hasRole(['Admin', 'HSE', 'Directeur', 'RH', 'Manager']) && (
              <Link to="/collaborateurs/nouveau">
                <Button variant="outline" className="w-full h-20 flex flex-col gap-2">
                  <Users className="h-6 w-6" />
                  <span className="text-sm">Nouveau collaborateur</span>
                </Button>
              </Link>
            )}
            {hasRole(['Admin', 'HSE', 'Directeur']) && (
              <Link to="/nouvelle-autorisation">
                <Button variant="outline" className="w-full h-20 flex flex-col gap-2">
                  <FileCheck className="h-6 w-6" />
                  <span className="text-sm">Nouvelle autorisation</span>
                </Button>
              </Link>
            )}
            {hasRole(['Admin', 'HSE', 'Manager']) && (
              <Link to="/epi/nouvelle-demande">
                <Button variant="outline" className="w-full h-20 flex flex-col gap-2">
                  <Package className="h-6 w-6" />
                  <span className="text-sm">Demande HSE</span>
                </Button>
              </Link>
            )}
            {hasRole(['Admin', 'HSE', 'Manager', 'Maintenance']) && (
              <Link to="/materiel">
                <Button variant="outline" className="w-full h-20 flex flex-col gap-2">
                  <Truck className="h-6 w-6" />
                  <span className="text-sm">Matériel</span>
                </Button>
              </Link>
            )}
            {hasRole(['Admin', 'RH', 'HSE']) && (
              <Link to="/visites-medicales">
                <Button variant="outline" className="w-full h-20 flex flex-col gap-2">
                  <Stethoscope className="h-6 w-6" />
                  <span className="text-sm">Visites médicales</span>
                </Button>
              </Link>
            )}
            {hasRole(['Admin', 'HSE', 'Directeur', 'Manager']) && (
              <Link to="/demandes">
                <Button variant="outline" className="w-full h-20 flex flex-col gap-2">
                  <FileCheck className="h-6 w-6" />
                  <span className="text-sm">Autorisations en cours</span>
                </Button>
              </Link>
            )}
            {hasRole(['Admin', 'HSE', 'RH', 'Manager']) && (
              <Link to="/formations">
                <Button variant="outline" className="w-full h-20 flex flex-col gap-2">
                  <GraduationCap className="h-6 w-6" />
                  <span className="text-sm">Formations</span>
                </Button>
              </Link>
            )}
            {hasRole(['Admin', 'HSE', 'RH', 'Manager']) && (
              <Link to="/caces">
                <Button variant="outline" className="w-full h-20 flex flex-col gap-2">
                  <Shield className="h-6 w-6" />
                  <span className="text-sm">CACES / Habilitations</span>
                </Button>
              </Link>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
